<template>
  <div class="count-categories-tab">
    <!-- FT-192 (REQ-08): статьи платежей компании — что показывать в форме платежа и какие
         у статей подкатегории. Дерево приходит из store (обновляется по cable). -->
    <h3 class="mt-1">{{ $t("count_categories_tab.title") }}</h3>
    <p class="grey--text text--darken-1">{{ $t("count_categories_tab.subtitle") }}</p>

    <p v-if="!hasCategories" class="grey--text" data-testid="count-categories-empty">
      {{ $t("count_categories_tab.empty") }}
    </p>

    <template v-for="group in groups">
      <div v-if="group.cards.length > 0" :key="group.key" :data-testid="'count-categories-group-' + group.key">
        <div class="text-subtitle-1 font-weight-medium mt-4 mb-2">{{ group.title }}</div>

        <v-card v-for="{ category, locked, treeAvailable, subs, activeCount, presets } in group.cards" :key="category.id"
          outlined class="mb-2"
          :data-testid="'count-category-' + category.id">
          <v-row dense align="center" class="px-2 py-1">
            <v-col cols="12" sm="7" class="d-flex align-center">
              <v-btn icon small :style="treeAvailable ? null : 'visibility: hidden'"
                :disabled="!treeAvailable" :tabindex="treeAvailable ? 0 : -1"
                :aria-hidden="treeAvailable ? null : 'true'"
                :aria-expanded="isExpanded(category) ? 'true' : 'false'"
                :aria-label="$t('count_categories_tab.subcategories')"
                @click="toggleExpanded(category)">
                <v-icon>{{ isExpanded(category) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </v-btn>
              <div class="ml-1">
                <div :class="{ 'grey--text': !isActive(category) }" class="font-weight-medium">
                  {{ label(category) }}
                </div>
                <div v-if="treeAvailable" class="caption grey--text">
                  {{ $t("count_categories_tab.subcategories_count", { count: activeCount }) }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="5" class="d-flex flex-column align-start align-sm-end">
              <v-switch :key="'switch-' + category.id + '-' + revision(category)" :input-value="isActive(category)"
                :disabled="locked || isBusy('c' + category.id)" :label="$t('count_categories_tab.in_form')"
                hide-details dense class="mt-0 pt-0" :data-testid="'count-category-switch-' + category.id"
                @change="toggleCategory(category, $event)"></v-switch>
              <div v-if="locked" class="caption grey--text mt-1" data-testid="count-category-locked-hint">
                {{ $t("count_categories_tab.base_locked_hint") }}
              </div>
            </v-col>
          </v-row>

          <v-expand-transition>
            <div v-if="treeAvailable && isExpanded(category)" class="px-4 pb-3">
              <v-divider class="mb-2"></v-divider>

              <p v-if="subs.length === 0" class="grey--text mb-2">
                {{ $t("count_categories_tab.no_subcategories") }}
              </p>

              <v-row v-for="sub in subs" :key="sub.id" dense align="center"
                :data-testid="'count-subcategory-' + sub.id">
                <v-col cols="12" sm="8">
                  <v-text-field v-if="editing && editing.id === sub.id" v-model="editing.value" dense hide-details autofocus
                    :label="$t('count_categories_tab.rename')" :disabled="isBusy('s' + sub.id)"
                    @keyup.enter="saveRename(category, sub)" @keyup.esc="cancelRename"></v-text-field>
                  <span v-else :class="{ 'grey--text': sub.active === false }">
                    {{ sub.value }}
                    <v-chip v-if="sub.active === false" x-small class="ml-2">{{ $t("count_categories_tab.hidden") }}</v-chip>
                  </span>
                </v-col>
                <v-col cols="12" sm="4" class="d-flex justify-start justify-sm-end">
                  <template v-if="editing && editing.id === sub.id">
                    <v-btn icon small color="primary" :aria-label="$t('custom.save')" :disabled="isBusy('s' + sub.id)"
                      @click="saveRename(category, sub)"><v-icon small>mdi-check</v-icon></v-btn>
                    <v-btn icon small :aria-label="$t('custom.cancel')" @click="cancelRename">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn icon small :aria-label="$t('count_categories_tab.rename')" @click="startRename(sub)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small :disabled="isBusy('s' + sub.id)"
                      :aria-label="sub.active === false ? $t('count_categories_tab.show') : $t('count_categories_tab.hide')"
                      :data-testid="'count-subcategory-toggle-' + sub.id" @click="toggleSubcategory(category, sub)">
                      <v-icon small>{{ sub.active === false ? "mdi-eye" : "mdi-eye-off" }}</v-icon>
                    </v-btn>
                  </template>
                </v-col>
              </v-row>

              <v-alert v-if="offers[category.id]" type="info" dense text outlined class="mt-2 mb-2"
                data-testid="count-subcategory-offer">
                {{ $t("count_categories_tab.show_hidden_offer", { name: offers[category.id].value }) }}
                <div class="mt-2">
                  <v-btn small color="primary" :disabled="isBusy('s' + offers[category.id].id)"
                    data-testid="count-subcategory-offer-show" @click="showOffered(category)">
                    {{ $t("count_categories_tab.show") }}
                  </v-btn>
                  <v-btn small text class="ml-2" @click="dismissOffer(category)">
                    {{ $t("count_categories_tab.dismiss") }}
                  </v-btn>
                </div>
              </v-alert>

              <v-row dense align="center" class="mt-2">
                <v-col cols="12" sm="8">
                  <v-text-field v-model="drafts[category.id]" dense hide-details
                    :label="$t('count_categories_tab.add_label')" :disabled="isBusy('a' + category.id)"
                    :data-testid="'count-subcategory-input-' + category.id"
                    @keyup.enter="addSubcategory(category, drafts[category.id])"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-btn color="primary" small block :loading="isBusy('a' + category.id)"
                    :disabled="!draftOf(category)" :data-testid="'count-subcategory-add-' + category.id"
                    @click="addSubcategory(category, drafts[category.id])">
                    <v-icon left small>mdi-plus</v-icon>{{ $t("count_categories_tab.add") }}
                  </v-btn>
                </v-col>
              </v-row>

              <div v-if="presets.length > 0" class="mt-3">
                <div class="caption grey--text mb-1">{{ $t("count_categories_tab.presets_label") }}</div>
                <v-chip v-for="preset in presets" :key="preset.key" small outlined class="mr-2 mb-2"
                  :disabled="isBusy('a' + category.id)" :data-testid="'count-preset-' + preset.key"
                  @click="addSubcategory(category, preset.label)">
                  <v-icon left x-small>mdi-plus</v-icon>{{ preset.label }}
                </v-chip>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>
    </template>
  </div>
</template>

<script>
import {
  canHideCategory,
  categoryCode,
  categoryKind,
  companyCountCategories,
  duplicateInfo,
  hasSubcategoryTree,
  sameId,
  settingsCategoryGroups,
  subcategoriesFor,
  translateCountCategory,
} from "@/utils/countCategory";
import { presetKeysFor } from "@/utils/countCategoryPresets";

function normalized(value) {
  return String(value === undefined || value === null ? "" : value).trim().toLowerCase();
}

export default {
  name: "CountCategoriesTab",
  data() {
    return {
      // Локальная копия дерева из store: правки видны сразу, cable пересобирает её.
      tree: [],
      expanded: {},
      drafts: {},
      offers: {},
      editing: null,
      busy: {},
      revisions: {},
    };
  },
  computed: {
    authHeaders() {
      return { headers: { Authorization: this.$store.getters.getAuthToken } };
    },
    storeCategories() {
      const getters = (this.$store && this.$store.getters) || {};
      return companyCountCategories(getters.getCompany);
    },
    // Группы с карточками статей: всё, что шаблон показывает по статье, считается здесь один раз
    // за изменение дерева, а не методами на каждую перерисовку.
    groups() {
      const { incomes, expenses } = settingsCategoryGroups(this.tree);
      return [
        { key: "incomes", title: this.$t("count_categories_tab.incomes"), cards: incomes.map(this.cardOf) },
        { key: "expenses", title: this.$t("count_categories_tab.expenses"), cards: expenses.map(this.cardOf) },
      ];
    },
    hasCategories() {
      return this.groups.some((group) => group.cards.length > 0);
    },
  },
  watch: {
    storeCategories: {
      immediate: true,
      handler(list) {
        this.tree = JSON.parse(JSON.stringify(list));
      },
    },
  },
  methods: {
    label(category) {
      return translateCountCategory((key) => this.$t(key), category.value);
    },
    isActive(category) {
      return category.active !== false;
    },
    // Карточка статьи для шаблона: подкатегории по алфавиту (скрытые тоже), счётчик видимых, чипы.
    cardOf(category) {
      const subs = this.subcategoriesOf(category);
      return {
        category,
        kind: categoryKind(category),
        locked: this.isLocked(category),
        treeAvailable: this.treeAvailable(category),
        subs,
        activeCount: subs.filter((sub) => sub.active !== false).length,
        presets: this.presetsFor(category, subs),
      };
    },
    // «Оплата аренды» (base) скрыть нельзя: на ней держатся бронирования и отчёты.
    isLocked(category) {
      return !canHideCategory(category);
    },
    treeAvailable(category) {
      return hasSubcategoryTree(category);
    },
    isExpanded(category) {
      return !!this.expanded[category.id];
    },
    toggleExpanded(category) {
      this.$set(this.expanded, category.id, !this.expanded[category.id]);
    },
    revision(category) {
      return this.revisions[category.id] || 0;
    },
    isBusy(key) {
      return !!this.busy[key];
    },
    subcategoriesOf(category) {
      return subcategoriesFor(category, { includeInactive: true });
    },
    draftOf(category) {
      return String(this.drafts[category.id] || "").trim();
    },
    // Чипы-подсказки статьи на языке пользователя, без уже заведённых (в том числе скрытых).
    // `subs` — подкатегории статьи, если уже посчитаны.
    presetsFor(category, subs = this.subcategoriesOf(category)) {
      const taken = new Set(subs.map((sub) => normalized(sub.value)));
      return presetKeysFor(categoryCode(category))
        .map((key) => ({ key, label: this.$t(`count_categories_tab.presets.${key}`) }))
        .filter((preset) => !taken.has(normalized(preset.label)));
    },
    // Один запрос на ключ за раз; busy снимается в finally.
    async guarded(key, work) {
      if (this.busy[key]) return false;
      this.$set(this.busy, key, true);
      try {
        return await work();
      } finally {
        this.$set(this.busy, key, false);
      }
    },
    async toggleCategory(category, value) {
      if (this.isLocked(category)) return;
      const active = !!value;
      await this.guarded("c" + category.id, async () => {
        try {
          await this.axios.patch(`/api/v1/counts_categories/${category.id}`, { counts_categories: { active } }, this.authHeaders);
          this.$set(category, "active", active);
        } catch (error) {
          // Переключатель уже перекинулся — пересоздаём его в прежнем положении.
          this.$set(this.revisions, category.id, this.revision(category) + 1);
          this.setError(error, this.$t("count_categories_tab.save_error"));
        }
      });
    },
    async addSubcategory(category, rawValue) {
      const value = String(rawValue || "").trim();
      if (value === "") return;
      await this.guarded("a" + category.id, async () => {
        try {
          const response = await this.axios.post(
            "/api/v1/counts_subcategories",
            { counts_subcategories: { category_code: categoryCode(category), value } },
            this.authHeaders
          );
          const created = response && response.data && response.data.id !== undefined ? response.data : null;
          if (created) {
            if (!Array.isArray(category.subcategories)) this.$set(category, "subcategories", []);
            category.subcategories.push({ id: created.id, value: created.value || value, active: created.active !== false });
          }
          this.$set(this.drafts, category.id, "");
          this.$delete(this.offers, category.id);
        } catch (error) {
          this.handleDuplicate(category, error, value);
        }
      });
    },
    // 422 с существующей подкатегорией: скрытую предлагаем показать, видимую — просто сообщаем.
    handleDuplicate(category, error, value) {
      const status = error && error.response && error.response.status;
      const duplicate = status === 422 ? duplicateInfo(error) : null;
      if (!duplicate) {
        this.setError(error, this.$t("count_categories_tab.save_error"));
        return;
      }
      const existing = this.findSubcategory(category, duplicate.id);
      const name = existing ? existing.value : value;
      if (duplicate.active === false) {
        this.$set(this.offers, category.id, { id: duplicate.id, value: name });
      } else {
        this.$notify(this.$t("count_categories_tab.already_exists", { name }), "", "warning", 4000);
      }
    },
    dismissOffer(category) {
      this.$delete(this.offers, category.id);
    },
    async showOffered(category) {
      const offer = this.offers[category.id];
      if (!offer) return;
      const ok = await this.patchSubcategory(category, offer.id, { active: true });
      if (ok) {
        this.$delete(this.offers, category.id);
        this.$set(this.drafts, category.id, "");
      }
    },
    findSubcategory(category, id) {
      return (category.subcategories || []).find((sub) => sub && sameId(sub.id, id));
    },
    // PATCH подкатегории; локальная копия обновляется после ответа сервера.
    async patchSubcategory(category, id, attrs) {
      return this.guarded("s" + id, async () => {
        try {
          const response = await this.axios.patch(`/api/v1/counts_subcategories/${id}`, { counts_subcategories: attrs }, this.authHeaders);
          const sub = this.findSubcategory(category, id);
          if (sub) {
            const saved = (response && response.data) || {};
            Object.keys(attrs).forEach((key) => this.$set(sub, key, saved[key] !== undefined ? saved[key] : attrs[key]));
          }
          return true;
        } catch (error) {
          this.handleDuplicate(category, error, attrs.value);
          return false;
        }
      });
    },
    async toggleSubcategory(category, sub) {
      await this.patchSubcategory(category, sub.id, { active: sub.active === false });
    },
    startRename(sub) {
      this.editing = { id: sub.id, value: sub.value };
    },
    cancelRename() {
      this.editing = null;
    },
    async saveRename(category, sub) {
      if (!this.editing || this.editing.id !== sub.id) return;
      const value = String(this.editing.value || "").trim();
      if (value === "" || value === sub.value) {
        this.editing = null;
        return;
      }
      const ok = await this.patchSubcategory(category, sub.id, { value });
      if (ok) this.editing = null;
    },
  },
};
</script>
